import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { TrendingUp, TrendingDown, Euro, Users, Clock, RefreshCw } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("sl-SI", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n || 0));

/** MRR / ARR / Churn / Growth analytics for super-admin. */
export const SuperAdminAnalytics = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/analytics`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Napaka pri branju analitike"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (loading || !data) {
    return <div className="flex items-center justify-center py-12"><div className="spinner" /></div>;
  }

  const maxGrowth = Math.max(1, ...data.growth_last_6mo.map(m => m.new_lds));
  const churnColor = data.churn_rate_pct >= 5 ? "text-red-600" : data.churn_rate_pct >= 2 ? "text-amber-600" : "text-green-600";

  return (
    <div className="space-y-4" data-testid="sa-analytics">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-primary">Analitika platforme</h2>
          <p className="text-xs text-muted-foreground">MRR, ARR, rast in odliv (churn) v zadnjih 30 dneh.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="sa-analytics-refresh"><RefreshCw className="h-4 w-4 mr-1" />Osveži</Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Euro className="h-3.5 w-3.5" /> MRR
            </div>
            <p className="text-2xl font-bold text-primary" data-testid="sa-mrr">{eur(data.mrr_eur)}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">ARR: {eur(data.arr_eur)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Users className="h-3.5 w-3.5" /> Aktivne
            </div>
            <p className="text-2xl font-bold text-green-700" data-testid="sa-active-count">{data.lds_active}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {data.monthly_subs} mesečnih · {data.yearly_subs} letnih
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Clock className="h-3.5 w-3.5" /> Preizkusi
            </div>
            <p className="text-2xl font-bold text-amber-600">{data.lds_trial}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">V teku (do 30 dni)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              {data.churn_rate_pct >= 2 ? <TrendingDown className="h-3.5 w-3.5" /> : <TrendingUp className="h-3.5 w-3.5" />} Churn 30d
            </div>
            <p className={`text-2xl font-bold ${churnColor}`} data-testid="sa-churn-rate">{data.churn_rate_pct}%</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{data.churned_30d} odlivov · {data.lds_expired} preteklo</p>
          </CardContent>
        </Card>
      </div>

      {/* Growth bar chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Nove LD registracije – zadnjih 6 mesecev</CardTitle>
          <CardDescription className="text-xs">Skupaj: {data.growth_last_6mo.reduce((a, b) => a + b.new_lds, 0)}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-between gap-1.5 h-32" data-testid="sa-growth-chart">
            {data.growth_last_6mo.map(m => {
              const h = (m.new_lds / maxGrowth) * 100;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                  <span className="text-[10px] font-medium text-primary">{m.new_lds}</span>
                  <div
                    className="w-full bg-gradient-to-t from-primary to-primary/60 rounded-t transition-all"
                    style={{ height: `${Math.max(h, 3)}%` }}
                    title={`${m.month}: ${m.new_lds}`}
                  />
                  <span className="text-[9px] text-muted-foreground truncate w-full text-center">{m.month.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Landing engagement / demo interest — last 30 days */}
      {data.demo_interest_30d && (
        <Card data-testid="sa-demo-interest">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Zanimanje za terensko predstavitev – zadnjih 30 dni</CardTitle>
            <CardDescription className="text-xs">Anonymous, GDPR-safe events iz javne landing strani</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-center">
                <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-medium">Klici (📞)</div>
                <div className="text-2xl font-bold text-emerald-800 mt-0.5" data-testid="sa-demo-phone-clicks">{data.demo_interest_30d.phone_clicks}</div>
              </div>
              <div className="bg-sky-50 border border-sky-100 rounded-lg p-3 text-center">
                <div className="text-[10px] uppercase tracking-wide text-sky-700 font-medium">E-poštni kliki</div>
                <div className="text-2xl font-bold text-sky-800 mt-0.5" data-testid="sa-demo-email-clicks">{data.demo_interest_30d.email_clicks}</div>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-center">
                <div className="text-[10px] uppercase tracking-wide text-amber-700 font-medium">Ogledi strani</div>
                <div className="text-2xl font-bold text-amber-800 mt-0.5" data-testid="sa-demo-impressions">{data.demo_interest_30d.impressions}</div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                <div className="text-[10px] uppercase tracking-wide text-slate-600 font-medium">Skupaj</div>
                <div className="text-2xl font-bold text-slate-800 mt-0.5" data-testid="sa-demo-total">{data.demo_interest_30d.total}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/** Audit log viewer for super-admin. */
export const SuperAdminAuditLog = () => {
  const { token } = useAuth();
  const [data, setData] = useState({ entries: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100", offset: "0" });
      if (action) params.set("action", action);
      const res = await axios.get(`${API}/superadmin/audit-log?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Napaka pri branju dnevnika"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [action]); // eslint-disable-line

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("sl-SI", { dateStyle: "short", timeStyle: "short" }) : "";
  const actionLabel = (a) => ({
    "ld.activate": "Aktivacija LD",
    "ld.deactivate": "Deaktivacija LD",
    "ld.update": "Sprememba LD",
    "ld.register": "Registracija LD",
    "sa.password_change": "Sprememba SA gesla",
    "sa.dedupe": "Odstranitev duplikatov",
    "subscription.manual": "Ročna naročnina",
  })[a] || a;

  const filterOptions = [
    { value: "", label: "Vse akcije" },
    { value: "ld.", label: "Akcije nad LD" },
    { value: "sa.", label: "Super-admin akcije" },
    { value: "subscription.", label: "Naročnine" },
  ];

  return (
    <div className="space-y-4" data-testid="sa-audit-log">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-primary">Dnevnik dejavnosti</h2>
          <p className="text-xs text-muted-foreground">Zadnjih {data.entries.length} od {data.total} dogodkov.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {filterOptions.map(opt => (
            <Button
              key={opt.value}
              size="sm"
              variant={action === opt.value ? "default" : "outline"}
              onClick={() => setAction(opt.value)}
              data-testid={`audit-filter-${opt.value || "all"}`}
            >{opt.label}</Button>
          ))}
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
      ) : data.entries.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground text-sm">Ni dogodkov v dnevniku.</CardContent></Card>
      ) : (
        <div className="space-y-1.5 max-h-[65vh] overflow-y-auto pr-1">
          {data.entries.map(e => (
            <div key={e.id} className="p-3 border rounded-lg hover:bg-muted/30 text-sm" data-testid={`audit-entry-${e.id}`}>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-semibold text-primary">{actionLabel(e.action)}</span>
                {e.target_name && <span className="text-muted-foreground">→ {e.target_name}</span>}
                <span className="ml-auto text-xs text-muted-foreground">{fmt(e.timestamp)}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-3">
                {e.actor_name && <span>Izvedel: <b>{e.actor_name}</b> ({e.actor_role})</span>}
                {!e.actor_name && <span className="italic">Sistem / javna registracija</span>}
                {e.details && Object.keys(e.details).length > 0 && (
                  <span className="text-[11px] font-mono bg-muted/50 px-2 py-0.5 rounded">
                    {Object.entries(e.details).slice(0, 3).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
